<div class="container mt-3 mb-4">
  <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
    <h4 class="m-0">Manage Guest</h4>
    <button class="btn btn-outline-secondary btn-sm" (click)="loadManageableProperties()">
      Refresh
    </button>
  </div>

  @if (isLoadingProperties()) {
    <div class="text-center py-4">
      <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
      <span class="ms-2">Loading manageable properties...</span>
    </div>
  }

  @if (!isLoadingProperties() && propertiesMessage()) {
    <div class="alert alert-info">{{ propertiesMessage() }}</div>
  }

  @if (manageableProperties().length > 1) {
    <div class="card p-3 mb-3">
      <label class="form-label" for="guestPropertySelect">Select Property</label>
      <select id="guestPropertySelect" class="form-select" [ngModel]="selectedPropertyId()" (ngModelChange)="onPropertyChanged($event)">
        <option [ngValue]="null">Choose Property</option>
        @for (property of manageableProperties(); track property.id) {
          <option [ngValue]="property.id">{{ property.name }}</option>
        }
      </select>
    </div>
  }

  @if (manageableProperties().length == 1) {
    <div class="card p-3 mb-3 bg-light">
      <div class="small text-muted">Managed Property</div>
      <div class="fw-semibold">{{ manageableProperties()[0].name }}</div>
    </div>
  }

  @if (manageableProperties().length > 0) {
    <div class="card p-3 mb-3">
      <div class="d-flex align-items-center gap-2 mb-2">
        <fa-icon [icon]="faFilter"></fa-icon>
        <span class="small fw-semibold">Filter by status</span>
      </div>
      <div class="d-flex flex-wrap gap-2">
        @for (status of statusFilters; track status) {
          <button class="btn btn-sm" [ngClass]="selectedStatusFilter() === status ? 'btn-primary' : 'btn-outline-primary'" (click)="setStatusFilter(status)">
            {{ status }}
          </button>
        }
      </div>
    </div>
  }

  @if (selectedPropertyId()) {
    @if (isLoadingGuests()) {
      <div class="text-center py-4">
        <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
        <span class="ms-2">Loading guest requests...</span>
      </div>
    }

    @if (!isLoadingGuests() && guestsMessage()) {
      <div class="alert alert-secondary">{{ guestsMessage() }}</div>
    }

    @if (!isLoadingGuests() && !guestsMessage()) {
      @for (guest of filteredGuests(); track guest.id) {
        <div class="card mb-3">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-start flex-wrap gap-2">
              <div>
                <h6 class="mb-1">{{ guest.firstname }} {{ guest.lastname }}</h6>
                <div class="text-muted small">{{ guest.email || '-' }}</div>
                <div class="small mt-1">Access key: {{ guest.accesskey || '-' }}</div>
              </div>
              <div>
                @if (approvalStatus(guest.approved) === 'Approved') {
                  <span class="badge bg-success">Approved</span>
                }
                @if (approvalStatus(guest.approved) === 'Denied') {
                  <span class="badge bg-danger">Denied</span>
                }
                @if (approvalStatus(guest.approved) === 'Pending') {
                  <span class="badge bg-warning text-dark">Pending</span>
                }
              </div>
            </div>

            <div class="row mt-3 g-2">
              <div class="col-md-4">
                <div class="border rounded p-2 small"><strong>Start:</strong> {{ formatApiDateTimeForDisplayLocal(guest.startdtime) }}</div>
              </div>
              <div class="col-md-4">
                <div class="border rounded p-2 small"><strong>Stop:</strong> {{ formatApiDateTimeForDisplayLocal(guest.stopdtime) }}</div>
              </div>
              <div class="col-md-4">
                <div class="border rounded p-2 small"><strong>Usage(s):</strong> {{ guest.numberofusages ?? '-' }}</div>
              </div>
              <div class="col-md-12">
                <div class="border rounded p-2 small"><strong>Devices/Gates:</strong> {{ formatGuestDevicesForDisplay(guest.devices) }}</div>
              </div>
              <div class="col-md-12">
                <div class="border rounded p-2 small"><strong>Advanced Schedule:</strong> {{ guest.crontimeconfig || 'No advanced schedule selected' }}</div>
              </div>
            </div>

            <div class="d-flex flex-wrap gap-2 mt-3">
              @if (!isProcessed(guest.approved)) {
                <button class="btn btn-success btn-sm" (click)="openApprove(guest)">
                  Approve <fa-icon class="ms-1" [icon]="faCheckCircle"></fa-icon>
                </button>
                <button class="btn btn-outline-danger btn-sm" (click)="denyGuestNow(guest.id)">
                  Deny <fa-icon class="ms-1" [icon]="faTimesCircle"></fa-icon>
                </button>
              }

              @if (canRevokeGuest(guest.stopdtime)) {
                <button class="btn btn-outline-warning btn-sm" (click)="revokeGuestAccess(guest)">
                  Revoke <fa-icon class="ms-1" [icon]="faRotateLeft"></fa-icon>
                </button>
              }
            </div>

            @if (approvalGuestId() === guest.id) {
              <div class="approval-panel mt-3 p-3 border rounded">
                <form [formGroup]="approveForm" (ngSubmit)="approveGuest(guest.id)">
                  <div class="row g-2">
                    <div class="col-md-6">
                      <label class="form-label">Start Date/Time</label>
                      <input type="datetime-local" class="form-control" formControlName="startdtime">
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">Stop Date/Time</label>
                      <input type="datetime-local" class="form-control" formControlName="stopdtime">
                    </div>
                    <div class="col-md-4">
                      <label class="form-label">Number of usages</label>
                      <select class="form-select" formControlName="usageOption">
                        @for (option of usageOptions; track option) {
                          <option [value]="option">{{ option }}</option>
                        }
                      </select>
                    </div>
                    @if (approveForm.value.usageOption === 'Custom') {
                      <div class="col-md-4">
                        <label class="form-label">Custom usages (1-20)</label>
                        <input type="number" class="form-control" formControlName="customUsage" min="1" max="20">
                      </div>
                    }
                    <div class="col-md-4 d-flex align-items-end">
                      <div class="form-check">
                        <input type="checkbox" class="form-check-input" id="advancedCron{{guest.id}}" [checked]="cronEnabled()" (change)="cronEnabled.set($any($event.target).checked)">
                        <label class="form-check-label" for="advancedCron{{guest.id}}">Use advanced daily schedule</label>
                      </div>
                    </div>
                  </div>

                  @if (cronEnabled()) {
                    <div class="row g-2 mt-1">
                      <div class="col-md-6">
                        <label class="form-label">Daily Start Time</label>
                        <input type="time" class="form-control" formControlName="dailyStartTime">
                      </div>
                      <div class="col-md-6">
                        <label class="form-label">Daily Stop Time</label>
                        <input type="time" class="form-control" formControlName="dailyStopTime">
                      </div>
                    </div>
                  }

                  <div class="mt-3">
                    <label class="form-label">Devices / Gates</label>
                    @if (isLoadingDevices()) {
                      <div class="small text-muted">Loading devices...</div>
                    }
                    @if (!isLoadingDevices() && propertyDevices().length === 0) {
                      <div class="small text-muted">{{ devicesMessage() || 'No devices available for this property.' }}</div>
                    }
                    @if (!isLoadingDevices() && propertyDevices().length > 0) {
                      <div class="row g-1">
                        @for (device of propertyDevices(); track device.id) {
                          <div class="col-md-6">
                            <div class="device-option border rounded p-2 d-flex align-items-center">
                              <input type="checkbox" class="form-check-input" id="device{{guest.id}}-{{device.id}}"
                                [checked]="selectedDeviceIds().includes($any(device.id))"
                                (change)="toggleDeviceSelection($any(device.id), $any($event.target).checked)">
                              <label class="form-check-label ms-2 mb-0" for="device{{guest.id}}-{{device.id}}">
                                {{ device.labeltxt || device.name || 'Unnamed gate' }}
                              </label>
                            </div>
                          </div>
                        }
                      </div>
                    }
                  </div>

                  <div class="d-flex flex-wrap gap-2 mt-3">
                    <button type="submit" class="btn btn-success btn-sm">Confirm Approve</button>
                    <button type="button" class="btn btn-outline-secondary btn-sm" (click)="cancelApprove()">Cancel</button>
                  </div>
                </form>
              </div>
            }
          </div>
        </div>
      }

      @if (filteredGuests().length === 0) {
        <div class="alert alert-light border">No {{ selectedStatusFilter().toLowerCase() }} guest requests found.</div>
      }
    }
  }
</div>
